<script lang="ts" setup>
import type { Timeframe } from '#core/types/date-filter'
import type { TimeframePickerOption } from '@forgd/ui-kit/blocks/timeframe-picker'
import type { Component } from 'vue'
import { useQueryProjectLogs } from '#trade-activity/queries/logs'
import { useTaStore } from '#trade-activity/stores/analyze-trade-activity'
import { getDateRange } from '#core/utils/dates'
import { formatValue } from '@forgd/formatter'
import { Badge } from '@forgd/ui-kit/badge'
import { DateInput } from '@forgd/ui-kit/blocks/date-input'
import { TimeframePicker } from '@forgd/ui-kit/blocks/timeframe-picker'
import { Button } from '@forgd/ui-kit/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@forgd/ui-kit/dialog'
import { Label } from '@forgd/ui-kit/label'
import { SectionHeader, SectionHeaderDescription, SectionHeaderTitle } from '@forgd/ui-kit/section-header'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { Spinner } from '@forgd/ui-kit/spinner'
import { AdjustmentsVerticalIcon, BookOpenIcon, BuildingLibraryIcon, CalendarIcon, ChartBarSquareIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'
import { createReusableTemplate } from '@vueuse/core'
import { endOfDay, format, parse, startOfDay } from 'date-fns'

useHead({
  title: 'Analyze Trade Activity',
  htmlAttrs: {
    class: 'scroll-smooth',
  },
})

interface SectionLink {
  label: string
  to: string
  icon: Component
}

const navItems: SectionLink[] = [
  { label: 'Summary', to: '#summary', icon: ChartBarSquareIcon },
  { label: 'Exchange Breakdown', to: '#exchange-breakdown', icon: BuildingLibraryIcon },
  { label: 'Liquidity & Volume', to: '#liquidity-volume', icon: ChartBarSquareIcon },
]

const timeframeOptions: TimeframePickerOption<Timeframe>[] = [
  { label: 'Prev. 24h', value: '24h' },
  { label: 'Prev. 7d', value: '7d' },
  { label: 'Prev. 30d', value: '30d' },
  { label: 'Prev. 60d', value: '60d' },
]

const reportLogInitialState = ref<'add' | 'edit' | 'view'>('view')
const isTaLogSlideoverOpen = ref(false)

const auth = useAuth()
const store = useTaStore()
const route = useRoute()
const uiHeadingData = computed(() => {
  const heading = route.meta.uiHeading as {
    title: string
    text: string
  }

  return {
    title: heading?.title || `See the performance of ${store.displayTicker} across all exchanges`,
    text: heading?.text || 'Learn about historical performance, on price, volume and liquidity. See the average daily breakdown of your markets from different angles.',
  }
})

const availableDateRangeQuery = useQuery({
  ...buildApiQuery('/business-intelligence/available-date-range', {
    params: () => ({
      query: {
        coingeckoId: store.apiCoingeckoId!,
      },
    }),
  }),
  enabled: () => Boolean(store.apiCoingeckoId),
  refetchOnWindowFocus: false,
})

const { data: logData, isPending: isLogPending } = useQueryProjectLogs()

const showReportLog = computed(() => Boolean(auth.project?.tokenListed))

const projectEditors = useProjectEditors()

function openTaLogSlideover(initialState: typeof reportLogInitialState.value) {
  reportLogInitialState.value = initialState
  isTaLogSlideoverOpen.value = true
}

const totalLogs = computed(() => logData.value?.total || 0)
const latestLog = computed(() => logData.value?.items[0])

const availableDateRange = computed(() => {
  if (!availableDateRangeQuery.data.value) {
    return undefined
  }
  const now = new Date()
  const dateFormat = 'yyyy-MM-dd'
  return {
    minDate: parse(availableDateRangeQuery.data.value.minDate, dateFormat, now),
    maxDate: parse(availableDateRangeQuery.data.value.maxDate, dateFormat, now),
  }
})

const activeTimeframe = computed(() => store.dateRange.timeframe === 'custom' ? null : store.dateRange.timeframe)

function selectTimeframe(timeframe: Timeframe) {
  if (timeframe === store.dateRange.timeframe) {
    return
  }
  store.dateRange = getDateRange(timeframe)
}

const customDate = computed<string | null>({
  get() {
    return store.dateRange.timeframe === 'custom' ? format(store.dateRange.start, 'yyyy-MM-dd') : null
  },
  set(value) {
    if (!value) {
      return
    }
    // Parses the date in the user's timezone
    const day = parse(value, 'yyyy-MM-dd', new Date())
    store.dateRange = {
      start: startOfDay(day),
      end: endOfDay(day),
      timeframe: 'custom',
    }
  },
})

const minCustomDate = computed(() => availableDateRange.value ? format(availableDateRange.value.minDate, 'yyyy-MM-dd') : undefined)
const maxCustomDate = computed(() => availableDateRange.value ? format(availableDateRange.value.maxDate, 'yyyy-MM-dd') : undefined)
const isCustomDateDisabled = computed(() => !availableDateRange.value)

onMounted(() => {
  if (store.dateRange.timeframe !== 'custom') {
    store.dateRange = getDateRange(store.dateRange.timeframe)
  }
})

const [DefineFilters, ReuseFilters] = createReusableTemplate()
const filtersOpen = ref(false)
</script>

<template>
  <DefineFilters>
    <div class="flex flex-col gap-2">
      <Label class="text-xs font-normal text-placeholder">
        Token Selection
      </Label>
      <TaTokenSelect />
    </div>
    <Label class="text-xs font-normal text-placeholder">
      Monitoring Timeframe
    </Label>
    <div class="space-y-2">
      <TimeframePicker
        :model-value="activeTimeframe"
        :options="timeframeOptions"
        @update:model-value="selectTimeframe"
      />
      <DateInput
        v-model="customDate"
        align="start"
        no-future-dates
        :min="minCustomDate"
        :max="maxCustomDate"
        :disabled="isCustomDateDisabled"
      >
        <template #trigger>
          <Button
            data-testid="date-input-button"
            size="sm"
            block
            class="justify-between"
            :class="{ 'bg-white': !customDate }"
            :variant="customDate ? 'default' : 'outline'"
            :disabled="isCustomDateDisabled"
          >
            {{ customDate ? formatValue(customDate, 'date') : 'Select a date' }}
            <Spinner v-if="availableDateRangeQuery.isPending.value" />
            <CalendarIcon v-else aria-hidden="true" />
          </Button>
        </template>
      </DateInput>
    </div>
  </DefineFilters>

  <NuxtLoadingIndicator />
  <NuxtLayout
    name="dashboard"
    title="Analyze Trade Activity"
    icon="/icons/financial-analysis.svg"
    :page-header="{ description: 'Free Tools', title: 'Analyze Trade Activity', icon: '/icons/financial-analysis.svg' }"
  >
    <template #sidebar>
      <div class="relative h-full w-sidebar border-r border-default max-md:hidden">
        <div class="sticky top-0 h-[calc(100vh-100px)] max-h-screen divide-y divide-default overflow-y-auto">
          <div class="flex flex-col gap-5 px-3 py-5">
            <ReuseFilters />
          </div>
          <div class="flex flex-col gap-5 px-3 py-5">
            <Label class="text-xs font-normal text-placeholder">
              Dashboard Sections
            </Label>
            <nav class="text-xs">
              <ul class="space-y-1">
                <li v-for="item in navItems" :key="item.to">
                  <NuxtLink
                    :to="item.to"
                    class="flex h-8 w-full items-center gap-2 rounded-lg p-2 text-secondary transition hover:bg-neutral-600/30"
                  >
                    <component :is="item.icon" class="size-3.5 shrink-0" aria-hidden="true" />
                    <span class="grow text-left">{{ item.label }}</span>
                  </NuxtLink>
                </li>
              </ul>
            </nav>
          </div>
          <div class="flex flex-col gap-5 px-3 py-5">
            <template v-if="showReportLog">
              <div class="flex items-center gap-1 text-primal">
                <BookOpenIcon class="size-4 shrink-0" aria-hidden="true" />
                <span class="text-xs font-semibold">Report Log:</span>
                <span class="text-xs">{{ totalLogs }} {{ totalLogs === 1 ? 'Entry' : 'Entries' }}</span>
              </div>
              <div class="flex flex-col gap-2">
                <Skeleton v-if="isLogPending" class="h-8 w-full" />
                <template v-else-if="latestLog">
                  <div class="flex flex-col gap-2 rounded-lg bg-tertiary p-3 text-xs text-secondary">
                    Latest: {{ formatValue(latestLog.createdAt, 'date') }}
                    <Badge variant="neutral" size="sm" class="self-start">
                      {{ latestLog.category.name }}
                    </Badge>
                    <div class="line-clamp-3 text-xs whitespace-pre-line">
                      {{ latestLog.content }}
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      class="flex-1"
                      @click="openTaLogSlideover('view')"
                    >
                      View All Log Entries
                    </Button>
                    <Button
                      v-if="projectEditors.isActiveEditor"
                      size="sm"
                      variant="outline"
                      icon-only
                      aria-label="Add a log entry"
                      @click="openTaLogSlideover('add')"
                    >
                      <PlusIcon />
                    </Button>
                  </div>
                </template>
                <template v-else>
                  <div class="rounded-lg bg-tertiary p-3 text-xs text-secondary">
                    {{ projectEditors.isActiveEditor ? 'Start recording key moments of your project performance.' : 'Log entries will appear here once the first one is created by the admin.' }}
                  </div>
                  <Button
                    v-if="projectEditors.isActiveEditor"
                    size="sm"
                    variant="outline"
                    @click="openTaLogSlideover('add')"
                  >
                    Create Your First Log
                    <PlusIcon />
                  </Button>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <div class="py-5">
      <div class="mb-5 flex items-center gap-2 md:hidden">
        <Button size="sm" variant="outline" @click="filtersOpen = true">
          Filter
          <AdjustmentsVerticalIcon />
        </Button>
        <Button
          v-if="showReportLog"
          size="sm"
          variant="outline"
          @click="openTaLogSlideover('view')"
        >
          Report Log
          <BookOpenIcon />
        </Button>
      </div>
      <Dialog v-model:open="filtersOpen">
        <DialogContent class="w-[calc(100%-2rem)] max-w-129 gap-5 rounded-lg p-5">
          <DialogTitle class="flex items-center gap-2 pr-8 text-xs leading-none font-semibold text-primal">
            <AdjustmentsVerticalIcon class="size-4" />
            Filter
          </DialogTitle>
          <DialogDescription class="sr-only">
            Choose the token and the monitoring timeframe for the dashboard.
          </DialogDescription>
          <div class="flex flex-col gap-5">
            <ReuseFilters />
          </div>
        </DialogContent>
      </Dialog>
      <SectionHeader class="mb-10">
        <SectionHeaderTitle>{{ uiHeadingData.title }}</SectionHeaderTitle>
        <SectionHeaderDescription class="xl:w-1/2">
          {{ uiHeadingData.text }}
        </SectionHeaderDescription>
      </SectionHeader>
      <slot />
    </div>
    <TaLogSlideover v-if="showReportLog" v-model="isTaLogSlideoverOpen" :initial-state="reportLogInitialState" />
    <template #app-footer>
      <CoreAppFooterBlock />
    </template>
  </NuxtLayout>
</template>
